/**
 * 新版首页专用轻量 i18n 内核（自 vancheen-b2b 项目移植为 Vue 2 版本）
 * ---------------------------------------------------------------
 * 语言状态用 Vue.observable 实现全局响应式：切换后所有读取 locale 的
 * 模板/computed 自动重渲染。
 *
 * 与老工程语言体系的打通：
 *   - 初始化优先级：URL ?lang= → 老工程语言 Cookie（language）→
 *     新站 localStorage（tw-locale）→ 浏览器语言（非中文回落 en）
 *   - setLocale 同时写 Cookie 与 localStorage，保证刷新后两套体系一致。
 */
import Vue from 'vue'
import { messages } from './nh-messages'

export const SUPPORTED_LOCALES = ['en', 'zh']
export const LOCALE_LABELS = { en: 'EN', zh: '中文' }

const STORAGE_KEY = 'tw-locale'
const LANG_COOKIE = 'language'

function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=(en|zh)\\b'))
  return m ? m[1] : null
}

function detectLocale() {
  // 1) 显式指定优先：?lang=en / ?lang=zh
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang')
    if (SUPPORTED_LOCALES.indexOf(fromUrl) > -1) return fromUrl
  } catch (e) { /* ignore */ }
  try {
    // 2) 老工程（vancheen-pc）当前语言
    const fromCookie = readCookie(LANG_COOKIE)
    if (fromCookie && SUPPORTED_LOCALES.indexOf(fromCookie) > -1) return fromCookie
    // 3) 新站历史选择
    const saved = localStorage.getItem(STORAGE_KEY)
    if (SUPPORTED_LOCALES.indexOf(saved) > -1) return saved
  } catch (e) { /* 隐私模式等场景忽略 */ }
  // 4) 跟随浏览器语言，非中文一律回落英文
  const nav = (navigator.language || '').toLowerCase()
  return nav.indexOf('zh') === 0 ? 'zh' : 'en'
}

const state = Vue.observable({ locale: detectLocale() })

export function currentLocale() {
  return state.locale
}

export function isZh() {
  return state.locale === 'zh'
}

export function setLocale(next) {
  if (SUPPORTED_LOCALES.indexOf(next) < 0 || next === state.locale) return
  state.locale = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch (e) { /* ignore */ }
  try {
    document.cookie = LANG_COOKIE + '=' + next + ';path=/;max-age=31536000'
  } catch (e) { /* ignore */ }
}

/** 按点路径取词；缺失时回落英文，再缺失则原样返回 key */
export function t(path) {
  const read = dict => {
    let node = dict
    const segs = path.split('.')
    for (let i = 0; i < segs.length; i++) {
      if (node == null) return undefined
      node = node[segs[i]]
    }
    return node
  }
  const hit = read(messages[state.locale])
  if (hit !== undefined) return hit
  const fallback = read(messages.en)
  return fallback !== undefined ? fallback : path
}

/**
 * 各页面双语标题（对应 vancheen-b2b 路由 meta 的 title / titleZh）
 */
const PAGE_TITLES = {
  home: {
    en: 'VANCHEEN — Auto Parts & Building Materials Supplier | B2B Export',
    zh: 'VANCHEEN — 汽车配件与建筑材料供应商 | B2B 出口'
  },
  about: {
    en: 'About Us — VANCHEEN | Auto Parts & Building Materials Supplier',
    zh: '关于我们 — VANCHEEN | 汽车配件与建筑材料供应商'
  },
  products: {
    en: 'Products — Auto Parts & Building Materials | VANCHEEN',
    zh: '产品中心 — 汽车配件与建筑材料 | VANCHEEN'
  },
  factory: {
    en: 'Factory Tour — Inside Our Partner Factories | VANCHEEN',
    zh: '工厂参观 — 走进合作工厂 | VANCHEEN'
  },
}

/** 设置页面标题（跟随语言） */
export function setPageTitle(pageKey) {
  const titles = PAGE_TITLES[pageKey]
  if (!titles) return
  document.title = titles[state.locale] || titles.en
}

export default { t, setLocale, currentLocale, isZh, setPageTitle, SUPPORTED_LOCALES, LOCALE_LABELS }
